import { useFormUiContext } from '@elementary/common-ui/components/AiChat';
import { UseFormReturn } from 'react-hook-form';
import { EnvironmentForm } from 'shared/types';
import { EnvironmentError } from '../../../../types';
import { CONTENT_STEP_INDEX } from '../constants';
import { getAssetSourceRules, getWarehouseTerms } from '../utils';
import {
  ENVIRONMENT_FORM_PROMPT,
  ENVIRONMENT_FORM_TOOL_NAME,
  ENVIRONMENT_FORM_UNAVAILABLE_REASONS,
} from './constants';
import {
  getEnvironmentFormFields,
  getEnvironmentFormHint,
  getEnvironmentFormNotes,
  getEnvironmentFormSteps,
  isWarehouseType,
} from './utils';

type Props = {
  methods: UseFormReturn<EnvironmentForm>;
  isOpen: boolean;
  currentStep: number;
  environmentId?: string;
  existingAssetSourceIds: string[];
  isSaving: boolean;
  isLoadingEnvironment: boolean;
  error?: EnvironmentError;
};

const useEnvironmentFormUiContext = ({
  methods,
  isOpen,
  currentStep,
  environmentId,
  existingAssetSourceIds,
  isSaving,
  isLoadingEnvironment,
  error,
}: Props) => {
  const isEditMode = !!environmentId;
  const steps = getEnvironmentFormSteps(isEditMode);
  const isContentStep = currentStep === CONTENT_STEP_INDEX;
  const step = isContentStep ? steps[1] : steps[0];

  useFormUiContext({
    name: ENVIRONMENT_FORM_TOOL_NAME,
    prompt: ENVIRONMENT_FORM_PROMPT,
    methods,
    isEnabled: isOpen,
    fields: getEnvironmentFormFields({ currentStep, existingAssetSourceIds }),
    getUnavailableReason: () => {
      if (isLoadingEnvironment) {
        return ENVIRONMENT_FORM_UNAVAILABLE_REASONS.loading;
      }
      return isSaving ? ENVIRONMENT_FORM_UNAVAILABLE_REASONS.saving : undefined;
    },
    getState: () => {
      const values = methods.getValues();
      const { warehouseType } = values;
      return {
        mode: isEditMode ? 'edit' : 'create',
        environmentId: environmentId ?? null,
        step,
        steps,
        hint: getEnvironmentFormHint({ isEditMode, stepIndex: step.index }),
        notes: getEnvironmentFormNotes(step.index, values),
        submitError: error ?? null,
        ...(isContentStep && isWarehouseType(warehouseType)
          ? {
              savedAssetSourceIds: existingAssetSourceIds,
              assetSourceRules: {
                ...getAssetSourceRules(warehouseType),
                ...getWarehouseTerms(warehouseType),
              },
            }
          : {}),
      };
    },
  });
};

export default useEnvironmentFormUiContext;
